import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiActivity, FiArrowDown, FiArrowUp, FiChevronLeft, FiHelpCircle } from 'react-icons/fi';
import { useFormik } from 'formik';
import { useClient } from 'jsonapi-react';
import moment from 'moment';
import { Loader } from '@/components/custom/loader';
import useReportAbilities from '@/hooks/useReportAbilities';
import usePermission from '@/hooks/usePermission';
import toast from 'react-hot-toast';
import { LineChartReport } from './LineChart';
import { UptimeTable } from './UptimeTable';
import { EmptyState } from '@/components/custom/empty-state';
import ReportForm from '@/components/custom/report-form-legacy';

export function UptimeMonthlyReport() {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState();
  const [chartData, setChartData] = useState([]);
  const [infos, setInfos] = useState();
  const [kpis, setKpis] = useState([]);
  const session = JSON.parse(localStorage.getItem('session'));
  const [selectedTab, setSelectedTab] = useState('24h');
  const client = useClient();
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const startYear = 2026;
  const currentYear = moment().year();
  const currentMonth = moment().format('MM');

  const yearOptions = Array.from({ length: currentYear - startYear + 1 }, (_, i) => {
    const year = startYear + i;
    return { id: year, name: String(year) };
  });

  const filters = useFormik({
    initialValues: {
      month: currentMonth,
      year: currentYear
    }
  });

  const { reportAbilities, getReportAbilities } = useReportAbilities();
  const [isDownloading, setIsDownloading] = useState(false);

  const endDate = moment(`${filters.values.year}-${filters.values.month}`, 'YYYY-MM').endOf('month').format('YYYY-MM-DD');

  const getReportData = async () => {
    setLoading(true);
    try {
      const response = await client.fetch(`uptime/stats?start_date=${filters.values.year}-${filters.values.month}-01&end_date=${endDate}`);
      setChartData(response?.['chart-data']);
      setInfos(response?.['response-time-stats']);

      const formattedTableData = Object.entries(response?.['incidents-by-day'] || {}).map(([date, incidents]) => ({
        date: moment(date).format('DD/MM/YYYY'),
        events: incidents
      }));
      setTableData(formattedTableData);
      setTotalCount(formattedTableData.reduce((sum, day) => sum + day.events.length, 0));
    } catch (error) {
      toast.error('Erro ao buscar dados do relatório.');
    }
    setLoading(false);
  };

  const getKpis = async () => {
    try {
      const response = await client.fetch('/uptime/kpis');
      setKpis(response);
    } catch (error) {
      toast.error('Erro ao buscar os KPIs de disponibilidade.');
    }
  };

  const apiKpis = [
    {
      id: 1,
      title: 'Últimos 7 dias',
      value: kpis?.['seven-days']?.['api-uptime-percentage'] !== null ? kpis?.['seven-days']?.['api-uptime-percentage'] + '%' : '-',
      totalCount: kpis?.['seven-days']?.['api-incidents-count'],
      variation: 'report-kpi-card--percent'
    },
    {
      id: 2,
      title: 'Últimos 30 dias',
      value: kpis?.['thirty-days']?.['api-uptime-percentage'] !== null ? kpis?.['thirty-days']?.['api-uptime-percentage'] + '%' : '-',
      totalCount: kpis?.['thirty-days']?.['api-incidents-count'],
      variation: 'report-kpi-card--percent'
    }
  ];
  const interfaceKpis = [
    {
      id: 1,
      title: 'Últimos 7 dias',
      value: kpis?.['seven-days']?.['front-uptime-percentage'] !== null ? kpis?.['seven-days']?.['front-uptime-percentage'] + '%' : '-',
      totalCount: kpis?.['seven-days']?.['front-incidents-count'],
      variation: 'report-kpi-card--percent'
    },
    {
      id: 2,
      title: 'Últimos 30 dias',
      value: kpis?.['thirty-days']?.['front-uptime-percentage'] !== null ? kpis?.['thirty-days']?.['front-uptime-percentage'] + '%' : '-',
      totalCount: kpis?.['thirty-days']?.['front-incidents-count'],
      variation: 'report-kpi-card--percent'
    }
  ];

  useEffect(() => {
    getReportAbilities();
    getKpis();
    getReportData();
  }, [filters.values.month, filters.values.year]);

  const getFilteredChartData = () => {
    if (!chartData) return [];

    const now = moment();

    const filtered = chartData.filter(item => {
      const itemTime = moment(item.timestamp);
      if (selectedTab === '1h') {
        return itemTime.isAfter(now.clone().subtract(1, 'hours'));
      }
      return itemTime.isAfter(now.clone().subtract(24, 'hours'));
    });

    return filtered.map(item => ({
      time: selectedTab === '24h' ? moment(item.timestamp).format('DD/MM HH:mm') : moment(item.timestamp).format('HH:mm'),
      interface: item['front-response-time'],
      api: item['api-response-time']
    }));
  };

  const handleDownload = () => {
    setIsDownloading(true);

    fetch(`${process.env.REACT_APP_DEV_URL}/uptime/incidents?start_date=${filters.values.year}-${filters.values.month}-01&end_date=${endDate}}`, {
      headers: {
        Authorization: `Bearer ${session?.user?.authToken}`
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Erro ao baixar o relatório');
        }
        return response.blob();
      })
      .then(blob => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `relatorio_disponibilidade_${moment().format('YYYYMMDDHHmmss')}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        toast.success('Relatório baixado com sucesso!');
      })
      .catch(error => {
        console.error('Erro no download:', error);
        toast.error('Erro ao baixar o relatório');
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  return (
    <div className="report">
      <header className="report__header">
        <h1 className="report__title">
          <button
            className="card__icon-wrapper"
            aria-label="Voltar"
            title="Voltar"
            onClick={() => navigate(-1)}
          >
            <FiChevronLeft className="card__icon" />
          </button>
          Relatório de disponibilidade da plataforma
        </h1>
        <p className="report__description">Este relatório acompanha o nível de disponibilidade do sistema ao longo do tempo, evidenciando possíveis instabilidades. As informações são atualizadas mensalmente, até o 2º dia útil.</p>
      </header>

      <div className="report__body">
        <p className="ml-auto text-sm text-gray-400">Última atualização em: {moment(kpis?.['last-updated-at']).format('DD/MM/YYYY HH:mm') || '--'}</p>

        {loading && <Loader />}

        {!loading && (
          <>
            <div className="report-widget">
              <div className="report-widget__header">
                <h3 className="report-widget__title">Visão geral</h3>
              </div>

              <div className="report-widget__body">
                <div className="report-kpi-card-wrapper">
                  <div className="flex-1 border-r-0 pr-0 md:border-r md:border-gray-200 md:pr-8">
                    <h4 className="mb-3 text-sm text-primary-dark">Disponibilidade da interface</h4>
                    <div className="flex flex-wrap gap-6">
                      {interfaceKpis.map(kpi => (
                        <div
                          className={`report-kpi-card ${kpi.variation}`}
                          key={kpi.id}
                        >
                          <h3 className="report-kpi-card__title">{kpi.title}</h3>
                          <p
                            className="report-kpi-card__value"
                            title={kpi.value}
                          >
                            {kpi.value}
                          </p>
                          <p className="text-sm text-gray-400">{kpi.totalCount !== null ? kpi.totalCount : '-'} ocorrências</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="mb-3 text-sm text-primary-dark">Disponibilidade da API</h4>
                    <div className="flex flex-wrap gap-6">
                      {apiKpis.map(kpi => (
                        <div
                          className={`report-kpi-card ${kpi.variation}`}
                          key={kpi.id}
                        >
                          <h3 className="report-kpi-card__title">{kpi.title}</h3>
                          <p
                            className="report-kpi-card__value"
                            title={kpi.value}
                          >
                            {kpi.value}
                          </p>
                          <p className="text-sm text-gray-400">{kpi.totalCount !== null ? kpi.totalCount : '-'} ocorrências</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {reportAbilities?.['visao-global-dos-cadastros-de-usuarios']?.['mapa-de-usuarios-cadastrados'] && (
              <div className="report-widget">
                <div className="report-widget__header">
                  <h3 className="report-widget__title">Tempo de resposta</h3>
                </div>
                <div className="w-fit p-1">
                  <button
                    onClick={() => setSelectedTab('1h')}
                    className={`rounded-l-full px-4 py-1.5 text-sm text-primary-dark transition ${selectedTab === '1h' ? 'bg-primary text-white shadow' : 'bg-neutral-bright text-gray-500'}`}
                  >
                    Última hora
                  </button>

                  <button
                    onClick={() => setSelectedTab('24h')}
                    className={`rounded-r-full px-4 py-1.5 text-sm text-primary-dark transition ${selectedTab === '24h' ? 'bg-primary text-white shadow' : 'bg-neutral-bright text-gray-500'}`}
                  >
                    Últimas 24 horas
                  </button>
                </div>

                <div className="flex flex-wrap justify-between gap-3">
                  <div className="flex flex-col">
                    <div className="flex gap-1">
                      <FiActivity className="text-primary" />
                      <span className="font-bold text-primary-dark">${infos?.['avg'] !== null ? infos?.['avg'] : '-'} ms</span>
                    </div>
                    <span className="text-gray-400">Tempo médio de resposta</span>
                  </div>
                  <div className="flex flex-col">
                    <div className="flex gap-1">
                      <FiArrowDown className="text-success" />
                      <span className="font-bold text-primary-dark">${infos?.['min'] !== null ? infos?.['min'] : '-'} ms</span>
                    </div>
                    <span className="text-gray-400">Resposta mais rápida que o tempo médio</span>
                  </div>
                  <div className="flex flex-col">
                    <div className="flex gap-1">
                      <FiArrowUp className="text-danger" />
                      <span className="font-bold text-primary-dark">${infos?.['max'] !== null ? infos?.['max'] : '-'} ms</span>
                    </div>
                    <span className="text-gray-400">Resposta mais lenta que o tempo médio</span>
                  </div>
                </div>

                <div className="report-widget__body">
                  {chartData?.length === 0 ? (
                    <div className="flex flex-col items-center py-28">
                      <EmptyState
                        variant="plain"
                        type="data"
                        text="Ainda não temos dados para mostrar"
                      />
                    </div>
                  ) : (
                    <LineChartReport report={getFilteredChartData()} />
                  )}
                </div>
              </div>
            )}

            <div className="report-widget">
              <div className="report-widget__header">
                <h3 className="report-widget__title">
                  Diário de disponibilidade - {moment(filters.values.month, 'MM').locale('pt-br').format('MMMM')} {filters.values.year}
                </h3>
                <div
                  className="report-widget__header"
                  style={{ justifyContent: 'flex-end', marginBottom: '24px' }}
                >
                  <div className="report-widget__filter">
                    {usePermission('Download de relatórios', 'Relatórios') && (
                      <button
                        className={`btn btn--outline ${isDownloading ? 'disabled' : ''}`}
                        type="button"
                        disabled={isDownloading}
                        onClick={handleDownload}
                      >
                        {isDownloading ? 'Baixando...' : 'Download do relatório'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap justify-between gap-3">
                <div className="flex flex-col">
                  <span className="font-bold text-primary-dark">{totalCount}</span>
                  <span className="text-gray-400">Total de ocorrências</span>
                </div>
                <div className="report__filter">
                  <ReportForm.Select
                    label="Mês"
                    value={filters.values.month}
                    onChange={e => filters.setFieldValue('month', e.target.value)}
                    options={[
                      { id: '01', name: 'Janeiro' },
                      { id: '02', name: 'Fevereiro' },
                      { id: '03', name: 'Março' },
                      { id: '04', name: 'Abril' },
                      { id: '05', name: 'Maio' },
                      { id: '06', name: 'Junho' },
                      { id: '07', name: 'Julho' },
                      { id: '08', name: 'Agosto' },
                      { id: '09', name: 'Setembro' },
                      { id: '10', name: 'Outubro' },
                      { id: '11', name: 'Novembro' },
                      { id: '12', name: 'Dezembro' }
                    ]}
                  />
                  <ReportForm.Select
                    label="Ano"
                    value={filters.values.year}
                    onChange={e => filters.setFieldValue('year', e.target.value)}
                    options={yearOptions}
                  />
                </div>
              </div>

              <div className="report-widget__body">
                <UptimeTable data={tableData} />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
